import { useCallback, useEffect, useRef, useState } from 'react';
import { useGameStore } from '@/store/gameStore';
import type { ColorBlindMode } from '@/accessibility/colorblind';

const colorBlindOptions: { value: ColorBlindMode; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'deuteranopia', label: 'Deuteranopia' },
  { value: 'protanopia', label: 'Protanopia' },
  { value: 'tritanopia', label: 'Tritanopia' },
];

export function Settings() {
  const settingsOpen = useGameStore((s) => s.settingsOpen);
  const setSettingsOpen = useGameStore((s) => s.setSettingsOpen);
  const colorBlindMode = useGameStore((s) => s.colorBlindMode);
  const setColorBlindMode = useGameStore((s) => s.setColorBlindMode);
  const reducedMotion = useGameStore((s) => s.reducedMotion);
  const setReducedMotion = useGameStore((s) => s.setReducedMotion);
  const showFrequencyNumbers = useGameStore((s) => s.showFrequencyNumbers);
  const setShowFrequencyNumbers = useGameStore((s) => s.setShowFrequencyNumbers);

  const panelRef = useRef<HTMLDivElement>(null);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);

  // Close on Escape key
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSettingsOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [settingsOpen, setSettingsOpen]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as HTMLElement)) {
        setSettingsOpen(false);
      }
    },
    [setSettingsOpen],
  );

  if (!settingsOpen) return null;

  return (
    <div style={backdropStyle} onClick={handleBackdropClick}>
      <div ref={panelRef} style={panelStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h3 style={titleStyle}>Settings</h3>
          <button
            style={{
              ...closeButtonStyle,
              ...(hoveredOption === 'close' ? closeButtonHoverStyle : {}),
            }}
            onClick={() => setSettingsOpen(false)}
            onMouseEnter={() => setHoveredOption('close')}
            onMouseLeave={() => setHoveredOption(null)}
          >
            {'\u2715'}
          </button>
        </div>

        {/* Visual section */}
        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Visual</h4>

          {/* Color blind mode */}
          <div style={settingRowStyle}>
            <span style={settingLabelStyle}>Color blind mode</span>
            <select
              style={selectStyle}
              value={colorBlindMode}
              onChange={(e) => setColorBlindMode(e.target.value as ColorBlindMode)}
            >
              {colorBlindOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Reduced motion */}
          <div style={settingRowStyle}>
            <span style={settingLabelStyle}>Reduced motion</span>
            <button
              style={{
                ...toggleStyle,
                ...(reducedMotion ? toggleOnStyle : {}),
              }}
              onClick={() => setReducedMotion(!reducedMotion)}
            >
              <span
                style={{
                  ...toggleKnobStyle,
                  ...(reducedMotion ? toggleKnobOnStyle : {}),
                }}
              />
            </button>
          </div>

          {/* Frequency numbers */}
          <div style={settingRowStyle}>
            <span style={settingLabelStyle}>Frequency labels</span>
            <button
              style={{
                ...toggleStyle,
                ...(showFrequencyNumbers ? toggleOnStyle : {}),
              }}
              onClick={() => setShowFrequencyNumbers(!showFrequencyNumbers)}
            >
              <span
                style={{
                  ...toggleKnobStyle,
                  ...(showFrequencyNumbers ? toggleKnobOnStyle : {}),
                }}
              />
            </button>
          </div>
        </div>

        {/* Audio section */}
        <div style={sectionStyle}>
          <h4 style={sectionTitleStyle}>Audio</h4>
          <div style={settingRowStyle}>
            <span style={settingLabelStyle}>Sound</span>
            <span style={comingSoonLabelStyle}>Coming Soon</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Gear button for opening settings */
export function SettingsButton(props: { style?: React.CSSProperties }) {
  const setSettingsOpen = useGameStore((s) => s.setSettingsOpen);
  const [hovered, setHovered] = useState(false);

  return (
    <button
      style={{
        ...gearButtonStyle,
        ...(hovered ? gearButtonHoverStyle : {}),
        ...props.style,
      }}
      onClick={() => setSettingsOpen(true)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label="Settings"
    >
      {'\u2699'}
    </button>
  );
}

// ── Styles ──

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(10, 10, 26, 0.75)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 200,
};

const panelStyle: React.CSSProperties = {
  background: 'rgba(20, 20, 40, 0.95)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '20px',
  padding: '28px 32px',
  maxWidth: '380px',
  width: '90%',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: 300,
  color: '#e8e8e4',
  letterSpacing: '0.1em',
  margin: 0,
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
};

const closeButtonStyle: React.CSSProperties = {
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: 'rgba(232,232,228,0.6)',
  fontSize: '14px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  outline: 'none',
};

const closeButtonHoverStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.1)',
  color: '#e8e8e4',
};

const sectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 500,
  color: 'rgba(199, 125, 255, 0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  margin: 0,
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
};

const settingRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 0',
};

const settingLabelStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'rgba(232,232,228,0.8)',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
};

const selectStyle: React.CSSProperties = {
  padding: '6px 12px',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '8px',
  color: '#e8e8e4',
  fontSize: '13px',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  outline: 'none',
  cursor: 'pointer',
};

const toggleStyle: React.CSSProperties = {
  width: '44px',
  height: '24px',
  borderRadius: '12px',
  background: 'rgba(255,255,255,0.1)',
  border: '1px solid rgba(255,255,255,0.15)',
  cursor: 'pointer',
  position: 'relative',
  transition: 'background 0.2s ease, border-color 0.2s ease',
  outline: 'none',
  padding: 0,
};

const toggleOnStyle: React.CSSProperties = {
  background: 'rgba(114, 9, 183, 0.4)',
  borderColor: 'rgba(114, 9, 183, 0.6)',
};

const toggleKnobStyle: React.CSSProperties = {
  position: 'absolute',
  top: '2px',
  left: '2px',
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  background: 'rgba(232,232,228,0.7)',
  transition: 'left 0.2s ease, background 0.2s ease',
};

const toggleKnobOnStyle: React.CSSProperties = {
  left: '22px',
  background: '#e8e8e4',
};

const comingSoonLabelStyle: React.CSSProperties = {
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'rgba(232,232,228,0.3)',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
};

const gearButtonStyle: React.CSSProperties = {
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.05)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: 'rgba(232,232,228,0.6)',
  fontSize: '18px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  outline: 'none',
  flexShrink: 0,
};

const gearButtonHoverStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.1)',
  color: '#e8e8e4',
};
